import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { loginModalState } from "@/app/layout/backdrop";
import { AlertDialog, ListItem, TopNavigation } from "@hamlak/ui";
import { logout } from "@/api/client";
import { LINKS, type DocLinkKey } from "@/config/links";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import { DefaultModelSheet } from "./DefaultModelSheet";
import { usePushSetting } from "@/features/push/usePushSetting";
import { useConsentSetting } from "./useConsentSetting";
import "@/features/me/me.css";
import "./settings.css";

/** 약관·정보 외부 링크 6종, 이 순서(AC0-230). 사업자 정보는 홈 푸터로 옮겨 여기서는 뺐다 */
const LINK_ROWS: readonly { key: DocLinkKey; label: () => string }[] = [
  { key: "terms", label: () => t("settings.terms") },
  { key: "privacy", label: () => t("settings.privacy") },
  { key: "operationPolicy", label: () => t("settings.operationPolicy") },
  { key: "youthPolicy", label: () => t("settings.youthPolicy") },
  { key: "refundPolicy", label: () => t("settings.refundPolicy") },
  { key: "notice", label: () => t("settings.notice") },
];

/** 표시용 앱 버전(웹 __APP_VERSION__, 네이티브 App.getInfo). build가 있으면 "1.2.3 (45)". */
export function useAppVersion(): string {
  const [version, setVersion] = useState("");
  useEffect(() => {
    let alive = true;
    void platform.app.getInfo().then((i) => {
      if (alive) setVersion(i.build !== null ? `${i.version} (${i.build})` : i.version);
    });
    return () => {
      alive = false;
    };
  }, []);
  return version;
}

/**
 * F07-05 설정(1.18). 회원: 알림(푸시 알림(앱, AC0-260) · 마케팅 정보 알림 · 야간 혜택 알림(웹·앱, 계정 단위)) · 기본 대화 모델 · 로그인 계정 · 약관·정책·공지 6종(Custom Tabs) · 고객센터 · 버전 · 로그아웃 · 회원 탈퇴.
 * 비회원: 같은 링크 6종·고객센터·버전(+ 로그인).
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const isMember = useAuthStore((s) => s.status === "member");
  const provider = useAuthStore((s) => s.provider);
  const publicId = useAuthStore((s) => s.user?.publicId ?? null);
  const defaultModel = useAuthStore((s) => s.settings?.defaultModel);
  const models = useConfigStore((s) => s.settings?.pricing.models);
  const requestLogin = useUiStore((s) => s.requestLogin);
  const version = useAppVersion();
  const push = usePushSetting();
  const consent = useConsentSetting(isMember);
  const [modelOpen, setModelOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const copyPublicId = async (id: string) => {
    try {
      await platform.device.copy(id);
      showSnackbar(t("me.publicId.copied"));
    } catch {
      showSnackbar(t("chat.copyFailed"));
    }
  };

  useEffect(() => {
  }, [isMember]);

  const modelName = models?.find((m) => m.id === defaultModel)?.name ?? models?.find((m) => m.isDefault)?.name ?? "";
  const providerLabel = provider === "apple" ? t("login.provider.apple") : provider === "google" ? t("settings.account.google") : "";

  const openLink = (key: DocLinkKey) => {
    void platform.browser.open(LINKS[key]);
  };

  const confirmLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      await platform.auth.signOut().catch(() => undefined);
      qc.clear();
    } finally {
      setLoggingOut(false);
      setLogoutOpen(false);
      navigate("/home", { replace: true });
    }
  };

  return (
    <main className="page" data-testid="settings-page">
      <TopNavigation title={t("page.settings")} right={null} onBack={() => navigate(-1)} />
      {isMember ? (
        <>
          {/* 시안 F07-05 "알림" 섹션 맨 위(43:1045·597:432). 마케팅 수신 동의·철회(정보통신망법 50조) */}
          <p className="me__listHeader me__listHeader--flush">{t("settings.section.notifications")}</p>
          <nav className="me__menu">
            {/* AC0-260 푸시 알림(앱 전용): 이 기기 푸시 켜기·끄기 */}
            {push.visible && <ListItem title={t("settings.push")} suffix={{ switch: push.on, onSwitch: push.toggle }} />}
            {consent.ready && (
              <ListItem
                title={t("settings.marketing")}
                suffix={{ switch: consent.agreed("marketing"), onSwitch: (next) => consent.toggle("marketing", next) }}
                disabled={consent.pending}
              />
            )}
            {/* 야간(21시~08시) 광고성 수신은 별도 동의(정보통신망법 50조 3항) */}
            {consent.ready && (
              <ListItem
                title={t("settings.nightMarketing")}
                suffix={{ switch: consent.agreed("night_marketing"), onSwitch: (next) => consent.toggle("night_marketing", next) }}
                disabled={consent.pending}
              />
            )}
          </nav>
          <p className="me__listHeader">{t("settings.section.general")}</p>
          <nav className="me__menu">
            <ListItem title={t("settings.defaultModel")} detail={modelName} onClick={() => {
              setModelOpen(true);
            }} />
            <ListItem title={t("settings.account")} detail={providerLabel} suffix="none" disabled />
            <ListItem title={t("me.publicId")} detail={publicId ?? "—"} suffix="none" aria-label={publicId ? t("me.publicId.copy", { id: publicId }) : undefined} onClick={publicId ? () => void copyPublicId(publicId) : undefined} disabled={!publicId} />
          </nav>
        </>
      ) : (
        <nav className="me__menu">
          <ListItem
            title={t("settings.login")}
            onClick={() => {
              requestLogin({ returnTo: "/settings" });
              navigate("/login", { state: loginModalState(location) });
            }}
          />
        </nav>
      )}
      <p className="me__listHeader">{t("settings.section.info")}</p>
      <nav className="me__menu">
        {LINK_ROWS.map((l) => (
          <ListItem key={l.key} title={l.label()} suffix="external" onClick={() => openLink(l.key)} />
        ))}
        <ListItem title={t("settings.support")} onClick={() => navigate("/support")} />
        <ListItem title={t("settings.version")} detail={version} suffix="none" disabled />
      </nav>
      {isMember && (
        <>
          {/* 시안 F07-05: 로그아웃·탈퇴 그룹은 헤더 없이 띠만 (43:1089) */}
          <div className="me__divider" aria-hidden />
          <nav className="me__menu">
            <ListItem title={t("settings.logout")} suffix="none" onClick={() => setLogoutOpen(true)} />
            <ListItem title={t("settings.delete")} suffix="none" className="settings__muted" onClick={() => navigate("/delete/notice")} />
          </nav>
          <DefaultModelSheet open={modelOpen} onClose={() => setModelOpen(false)} />
          <AlertDialog
            open={logoutOpen}
            title={t("settings.logout.title")}
            description={t("settings.logout.body")}
            primaryLabel={t("settings.logout.confirm")}
            secondaryLabel={t("common.cancel")}
            loading={loggingOut}
            onPrimary={() => void confirmLogout()}
            onSecondary={() => setLogoutOpen(false)}
          />
        </>
      )}
    </main>
  );
}
